// Product mode, read once from /api/session and cached by react-query.
//
// N2J_PRODUCT=uk-cn on the server turns on the Chinese-first UK-CN experience
// (Chinese UI, 职位板, 生成简历). Default '' keeps the original English product.
// One deployment serves both; the VM sets the env var to flip it.
//
// useZh() is the sugar most callers want: a boolean for "render Chinese".
// Because /api/session is a shared query key, this never double-fetches.

import { useQuery } from '@tanstack/react-query';
import { apiGet } from './api';

interface SessionProduct {
  product?: string; openSignup?: boolean; isAdmin?: boolean; multitenant?: boolean;
  emailEnabled?: boolean; email?: string | null;
}

function useSession() {
  return useQuery({
    queryKey: ['session'],
    queryFn: () => apiGet<SessionProduct>('/api/session'),
    staleTime: 5 * 60_000,
  });
}

export function useProduct(): string {
  return useSession().data?.product || '';
}

/**
 * False until /api/session has answered.
 *
 * `product` is '' while the request is in flight, which is indistinguishable
 * from "this deployment is not uk-cn". A route guard written as
 * `if (!useZh()) return <Navigate …/>` therefore fires on the very first render
 * and bounces the visitor away before the answer arrives — so deep links to
 * uk-cn-only pages silently landed somewhere else. Guards must wait for this.
 * Landing.tsx has always done the equivalent with its own `sessionQ.isLoading`,
 * for the same reason ("a flash of the welcome page").
 */
export function useProductReady(): boolean {
  return !useSession().isPending;
}

/** True when the UK-CN Chinese-first product is enabled. */
export function useZh(): boolean {
  return useProduct() === 'uk-cn';
}

/** True when self-serve signup is open (no invite code required). */
export function useOpenSignup(): boolean {
  return useSession().data?.openSignup === true;
}

/**
 * True when the visitor has an identity: a signed-in account in multitenant
 * mode, or the synthetic user of single-user mode. Anonymous visitors on the
 * public pages (the board, the landing page) are false.
 *
 * The discriminator is `email` — /api/session returns null for an anonymous
 * caller. Landing.tsx has always made this exact check inline; this is that
 * convention extracted, not a new one. Only meaningful after useProductReady().
 */
export function useSignedIn(): boolean {
  const s = useSession().data;
  return !!s && (s.multitenant !== true || !!s.email);
}

/**
 * True when the deployment can actually send email. When false, password-reset
 * links are only written to the server log, so the UI must not tell anyone to
 * go and check their inbox.
 */
export function useEmailEnabled(): boolean {
  return useSession().data?.emailEnabled === true;
}

/** True when the current session belongs to an admin (the maintainer). */
export function useIsAdmin(): boolean {
  return useSession().data?.isAdmin === true;
}

/** True in multi-tenant mode (real accounts). False in legacy single-user mode. */
export function useMultitenant(): boolean {
  return useSession().data?.multitenant === true;
}

/**
 * True when the maintainer-only English tools (Settings, Scanner config, …)
 * should be hidden from this user: the UK-CN student product is on AND the
 * user is not an admin. The flag is deployment-wide, so admins keep access
 * even when N2J_PRODUCT=uk-cn.
 */
export function useHideMaintainerTools(): boolean {
  const s = useSession().data;
  return s?.product === 'uk-cn' && s?.isAdmin !== true;
}
